/**
 * Proportional size distribution (PSD) metrics for a single species in one sampling session.
 * Denominator: fish >= species stock length (PsdStockIn / PsdStockCm), fallback 8 in / 20 cm.
 * Numerators: stock-length fish that are also >= each Psd* threshold (same unit path).
 */

import { formatDateTime } from "./formatters";

const FALLBACK_STOCK_CM = 20;
const FALLBACK_STOCK_IN = 8;

function normalizeLengthUnit(u) {
  return String(u || "inch").toLowerCase();
}

/** @returns {'english'|'metric'} */
export function getPsdEvaluationMode(pondLengthUnitName) {
  const u = normalizeLengthUnit(pondLengthUnitName);
  if (u.includes("inch")) return "english";
  return "metric";
}

/**
 * Resolve the PSD evaluation mode. Prefer the user-selected display system
 * ("English" | "Metric") so PSD matches the charts/table the user is viewing;
 * fall back to the session's storage unit when no system is supplied.
 * @param {string|{value?: string}} [displaySystem]
 * @param {string} pondLengthUnitName
 * @returns {'english'|'metric'}
 */
export function resolvePsdEvaluationMode(displaySystem, pondLengthUnitName) {
  const raw =
    typeof displaySystem === "object" && displaySystem
      ? displaySystem.value
      : displaySystem;
  const s = String(raw || "").toLowerCase();
  if (s === "english") return "english";
  if (s === "metric") return "metric";
  return getPsdEvaluationMode(pondLengthUnitName);
}

function lengthToCm(length, pondUnitLower) {
  const L = Number(length);
  if (!Number.isFinite(L)) return NaN;
  if (pondUnitLower.includes("inch")) return L * 2.54;
  if (pondUnitLower === "mm") return L / 10;
  if (pondUnitLower === "cm") return L;
  return L * 2.54;
}

function lengthToInches(length, pondUnitLower) {
  const L = Number(length);
  if (!Number.isFinite(L)) return NaN;
  if (pondUnitLower.includes("inch")) return L;
  if (pondUnitLower === "mm") return L * 0.0393701;
  if (pondUnitLower === "cm") return L / 2.54;
  return L;
}

function resolveStockCutoff(sample, mode) {
  if (mode === "metric") {
    const stockCm = Number(sample.PsdStockCm);
    return Number.isFinite(stockCm) && stockCm > 0 ? stockCm : FALLBACK_STOCK_CM;
  }
  const stockIn = Number(sample.PsdStockIn);
  return Number.isFinite(stockIn) && stockIn > 0 ? stockIn : FALLBACK_STOCK_IN;
}

/**
 * @param {Array<object>} fishRows - SamplingData rows (same shape as API): Length, FishTypeName, PsdQualityCm, ...
 * @param {string} pondLengthUnitName - detail.LengthUnitName (storage unit used to convert lengths)
 * @param {string|{value?: string}} [displaySystem] - selected "English"|"Metric"; defaults to storage-derived mode
 */
export function computePsdForSpeciesSession(
  fishRows,
  pondLengthUnitName,
  displaySystem
) {
  const mode = resolvePsdEvaluationMode(displaySystem, pondLengthUnitName);
  const u = normalizeLengthUnit(pondLengthUnitName);

  const sample = fishRows[0] || {};
  const stockCutoff = resolveStockCutoff(sample, mode);
  const qCm = Number(sample.PsdQualityCm);
  const pCm = Number(sample.PsdPreferredCm);
  const mCm = Number(sample.PsdMemorableCm);
  const tCm = Number(sample.PsdTrophyCm);
  const qIn = Number(sample.PsdQualityIn);
  const pIn = Number(sample.PsdPreferredIn);
  const mIn = Number(sample.PsdMemorableIn);
  const tIn = Number(sample.PsdTrophyIn);

  let denom = 0;
  let nQ = 0;
  let nP = 0;
  let nM = 0;
  let nT = 0;

  for (let i = 0; i < fishRows.length; i++) {
    const L =
      mode === "metric"
        ? lengthToCm(fishRows[i].Length, u)
        : lengthToInches(fishRows[i].Length, u);
    if (!Number.isFinite(L)) continue;
    if (L < stockCutoff) continue;
    denom += 1;
    if (mode === "metric") {
      if (Number.isFinite(qCm) && L >= qCm) nQ += 1;
      if (Number.isFinite(pCm) && L >= pCm) nP += 1;
      if (Number.isFinite(mCm) && L >= mCm) nM += 1;
      if (Number.isFinite(tCm) && L >= tCm) nT += 1;
    } else {
      if (Number.isFinite(qIn) && L >= qIn) nQ += 1;
      if (Number.isFinite(pIn) && L >= pIn) nP += 1;
      if (Number.isFinite(mIn) && L >= mIn) nM += 1;
      if (Number.isFinite(tIn) && L >= tIn) nT += 1;
    }
  }

  const pct = (num) => (denom > 0 ? (num / denom) * 100 : null);

  return {
    mode,
    nFish: fishRows.length,
    nDenominator: denom,
    psdQ: pct(nQ),
    psdP: pct(nP),
    psdM: pct(nM),
    psdT: pct(nT),
    nQ,
    nP,
    nM,
    nT,
  };
}

/** Keep only fields PSD needs so React state is not holding full EnrichSessions rows. */
export function compactPondDetailsForPsd(pondDetails) {
  return (pondDetails || []).map((d) => ({
    id: d.id,
    Date: d.Date,
    Name: d.Name,
    PondID: d.PondID,
    LengthUnitName: d.LengthUnitName,
    WeightUnitName: d.WeightUnitName,
    SamplingData: (d.SamplingData || []).map((f) => ({
      Length: f.Length,
      FishTypeName: f.FishTypeName,
      PsdStockCm: f.PsdStockCm,
      PsdStockIn: f.PsdStockIn,
      PsdQualityCm: f.PsdQualityCm,
      PsdPreferredCm: f.PsdPreferredCm,
      PsdMemorableCm: f.PsdMemorableCm,
      PsdTrophyCm: f.PsdTrophyCm,
      PsdQualityIn: f.PsdQualityIn,
      PsdPreferredIn: f.PsdPreferredIn,
      PsdMemorableIn: f.PsdMemorableIn,
      PsdTrophyIn: f.PsdTrophyIn,
    })),
  }));
}

/**
 * Group fish by FishTypeName and compute PSD per group.
 * @returns {Array<{ species: string, metrics: ReturnType<typeof computePsdForSpeciesSession> }>}
 */
export function computePsdTableRowsForSession(
  detail,
  selectedFishLabels,
  displaySystem
) {
  const bySpecies = new Map();
  const sampling = detail.SamplingData || [];
  sampling.forEach((fish) => {
    const name = fish.FishTypeName || "Unknown";
    if (
      selectedFishLabels.length > 0 &&
      !selectedFishLabels.some((l) => l === name)
    ) {
      return;
    }
    if (!bySpecies.has(name)) bySpecies.set(name, []);
    bySpecies.get(name).push(fish);
  });

  const pondUnit = detail.LengthUnitName;
  const rows = [];
  bySpecies.forEach((rowsFish, species) => {
    rows.push({
      species,
      metrics: computePsdForSpeciesSession(rowsFish, pondUnit, displaySystem),
    });
  });
  rows.sort((a, b) => a.species.localeCompare(b.species));
  return rows;
}

/**
 * Flat rows for PSD vs date chart and PSD data table across sessions.
 * @param {Array<object>} pondDetails
 * @param {Array<{ label: string }>} selectedFish - empty = all species
 */
export function buildPsdVsDateRows(pondDetails, selectedFish, displaySystem) {
  const labels = (selectedFish || []).map((f) => f.label).filter(Boolean);
  const rows = [];
  (pondDetails || []).forEach((detail) => {
    const perSpecies = computePsdTableRowsForSession(detail, labels, displaySystem);
    const dateLabel = formatDateTime(detail.Date);
    const dateMs = new Date(detail.Date).getTime();
    perSpecies.forEach(({ species, metrics }) => {
      rows.push({
        dateLabel,
        dateMs: Number.isNaN(dateMs) ? 0 : dateMs,
        species,
        psdQ: metrics.psdQ,
        psdP: metrics.psdP,
        psdM: metrics.psdM,
        psdT: metrics.psdT,
        nDenominator: metrics.nDenominator,
        nFish: metrics.nFish,
      });
    });
  });
  return rows.sort((a, b) => a.dateMs - b.dateMs || a.species.localeCompare(b.species));
}
